import { gql } from '@apollo/client';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { convertFromRaw } from 'draft-js';
import NextLink from 'next/link';
import * as React from 'react';

import { DialInInfo } from '@/components/DialInInfo';
import { ReadOnly, ReadOnlyValue } from '@/components/ReadOnly';
import { Redraft } from '@/components/Redraft';
import { VenueFragment } from '@/fragments/graphql-types/VenueFragment';
import { EventInteraction } from '@/graphql-types/globalTypes';
import { useTrackEventInteraction } from '@/hooks/useTrackEventInteraction';
import { Prose } from '@/imports/ui/chakra/components/Prose';
import { Semibold } from '@/imports/ui/chakra/components/Semibold';
import { ExternalLinkIcon } from '@/imports/ui/chakra/feather';
import { isVideoFileALink } from '@/utilities';

export const ModalToJoinExternalEvent: React.FC<{
  onClose: () => void;
  isOpen: boolean;
  venue: VenueFragment | null;
  isCurrentUserSpeaker: boolean;
  simpleEventOrConferenceId: string;
  meta:
  | { type: 'simple_event'; eventId: string; name: string }
  | { type: 'session'; sessionId: string; name: string }
  | { type: 'booth'; boothId: string; name: string }
  | { type: 'agoraMeet'; meetId: string; name: string };
  isJoiningWithSpecialPermissions: boolean;
  isFuture: boolean;
  isAgoraMeet?: boolean;
  meetId?: string;
  isPast: boolean;
  hasCanonicalVideo: boolean;
}> = (props) => {
  const { trackEventInteraction } = useTrackEventInteraction({
    eventMetadata:
      'eventId' in props.meta
        ? { simpleEventId: props.meta.eventId }
        : 'sessionId' in props.meta
          ? { conferenceSessionId: props.meta.sessionId }
          : props.meta.type === 'booth'
            ? { conferenceBoothId: props.meta.boothId }
            : { conferenceBoothId: props.meta.meetId }

  });


  const getVenueMessage = () => {
    // No venue data at all
    if (!props.venue) {
      return (
        <Text>
          This {{
            simple_event: 'event',
            session: 'session',
            booth: 'booth',
            agoraMeet: 'meet'
          }[props.meta.type]} will be available at the scheduled time.
        </Text>
      );
    }

    // Has venue but missing online details
    if (!props.venue.online) {
      return (
        <Text>
          This {{
            simple_event: 'event',
            session: 'session',
            booth: 'booth',
            agoraMeet: 'meet'
          }[props.meta.type]} will be hosted on Tradewing.
        </Text>
      );
    }

    // Default to null so other conditions in the component can handle it
    return null;
  };

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      isCentered
      scrollBehavior="inside"
      size="lg"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {{
            simple_event: 'Event',
            session: 'Session',
            booth: 'Booth',
            agoraMeet: 'Meet'
          }[props.meta.type]}{' '}
          Details
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            {props.isJoiningWithSpecialPermissions && (
              <Alert colorScheme="gray">
                <AlertIcon />
                {props.isFuture ? (
                  <AlertDescription>
                    <Semibold>
                      As{' '}
                      {props.isCurrentUserSpeaker
                        ? 'a speaker'
                        : 'an event admin'}
                      , you are able to preview this event early.
                    </Semibold>{' '}
                    Regular attendees can only join during the scheduled event
                    time.
                  </AlertDescription>
                ) : props.isPast ? (
                  <AlertDescription>
                    <Semibold>This event has ended.</Semibold> As{' '}
                    {props.isCurrentUserSpeaker
                      ? 'a speaker'
                      : 'an event admin'}
                    , you have permission to view the event page.
                  </AlertDescription>
                ) : (
                  <AlertDescription>
                    As{' '}
                    {props.isCurrentUserSpeaker
                      ? 'a speaker'
                      : 'an event admin'}
                    , you have permission to view the event page.
                  </AlertDescription>
                )}
              </Alert>
            )}

            {/* Display default venue message if other conditions don't apply */}
            {getVenueMessage() || (
              props.isPast && props.hasCanonicalVideo ? (
                <Text>
                  This{' '}
                  {{
                    simple_event: `event's`,
                    session: `session's`,
                    booth: `booth's`,
                    agoraMeet: `meet's`
                  }[props.meta.type]}{' '}
                  recording is available on <Semibold>Tradewing</Semibold>.
                </Text>
              ) : props.venue && props.venue.online ? (
                <Text>
                  This{' '}
                  {{
                    simple_event: 'event',
                    session: 'session',
                    booth: 'booth',
                    agoraMeet: 'meet'
                  }[props.meta.type]}{' '}
                  is hosted on{' '}
                  <Semibold>
                    {props.venue.online.__typename ===
                      'EventVenue_TradewingPlatform'
                      ? 'Tradewing'
                      : props.venue.online.externalPlatformName
                        ? props.venue.online.externalPlatformName
                        : 'an external platform'}
                  </Semibold>
                  .
                </Text>
              ) : null
            )}

            {/* {!props.isPast &&
  props.meta.type !== 'agoraMeet' &&  
  props.venue.online &&
  props.venue.online.__typename === 'EventVenue_TradewingPlatform' &&
  props.venue.online.stream.__typename ===
    'EventVenue_TradewingPlatform_Stream_Live' &&
  props.venue.online.stream.virtualEvent &&
  props.venue.online.stream.virtualEvent.dialInEnabled &&
  props.venue.online.stream.virtualEvent.dialInPhoneNumber && (
    <Box bg="gray.50" p="4" borderRadius="lg">
      <DialInInfo
        eventName={props.meta.name}
        dialInCode={props.venue.online.stream.virtualEvent.dialInCode}
        listenerDialInCode={props.venue.online.stream.virtualEvent.listenerDialInCode}
        dialInPhoneNumber={props.venue.online.stream.virtualEvent.dialInPhoneNumber}
        isCurrentUserSpeaker={props.isCurrentUserSpeaker}
        isParticipationAllowed={props.venue.online.stream.virtualEvent.maxParticipants > 0}
      />
    </Box>
  )} */}

            {props.venue && props.venue.online && !props.isPast &&
              props.venue.online.__typename === 'EventVenue_ExternalPlatform' &&
              props.venue.online.additionalDetails &&
              convertFromRaw(
                props.venue.online.additionalDetails
              ).getPlainText().length > 0 && (
                <Box bg="gray.50" p="4" borderRadius="lg">
                  <ReadOnly>
                    <ReadOnlyValue as={Prose}>
                      <Redraft
                        rawDraftContentState={
                          props.venue.online.additionalDetails
                        }
                      />
                    </ReadOnlyValue>
                  </ReadOnly>
                </Box>
              )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            colorScheme="gray"
            onClick={props.onClose}
            mr="2"
          >
            Close
          </Button>

          {props.venue && props.venue.online &&
            props.venue.online.__typename === 'EventVenue_TradewingPlatform' &&
            props.venue.online.stream &&
            props.venue.online.stream.__typename ===
            'EventVenue_TradewingPlatform_Stream_Prerecorded' &&
            props.venue.online.stream.videoFile &&
            isVideoFileALink(props.venue.online.stream.videoFile) ? (
            <NextLink
              href={props.venue.online.stream.videoFile._id}
              target="_blank"
            >
              <Button rightIcon={<ExternalLinkIcon />}>
                Watch on an External Platform
              </Button>
            </NextLink>
          ) : (props.venue && props.venue.online &&
            props.venue.online.__typename ===
            'EventVenue_TradewingPlatform') ||
            (props.isPast && props.hasCanonicalVideo) ? (
            ['simple_event', 'session', 'booth', 'agoraMeet'].includes(props.meta.type) ? (
              <NextLink
                href={
                  props.isAgoraMeet && props.meetId
                    ? `/events/${props.simpleEventOrConferenceId}/meet`
                    : props.meta.type === 'session'
                      ? `/events/conferences/${props.simpleEventOrConferenceId}/sessions/${props.meta.sessionId}`
                      : props.meta.type === 'booth'
                        ? `/events/conferences/${props.simpleEventOrConferenceId}/booth/${props.meta.boothId}`
                        : `/virtualEvent/${props.simpleEventOrConferenceId}`
                }
                target="_blank"
              >
                <Button rightIcon={<ExternalLinkIcon />}>
                  {props.isPast && props.hasCanonicalVideo
                    ? 'Watch on Tradewing'
                    : 'Join on Tradewing'}
                </Button>
              </NextLink>
            ) : null
          ) : props.venue && props.venue.online &&
            props.venue.online.__typename === 'EventVenue_ExternalPlatform' &&
            props.venue.online.url ? (
            <Button
              as="a"
              href={props.venue.online.url}
              target="_blank"
              rel="noopener"
              rightIcon={<ExternalLinkIcon />}
              onClick={() =>
                trackEventInteraction(
                  EventInteraction.CLICKED_EXTERNAL_EVENT_LINK
                )
              }
            >
              Join on{' '}
              {props.venue.online.externalPlatformName ??
                'an External Platform'}
            </Button>
          ) : (
            // Default Join button when venue data is incomplete
            <NextLink
              href={`/virtualEvent/${props.simpleEventOrConferenceId}`}
              target="_blank"
            >
              <Button rightIcon={<ExternalLinkIcon />}>
                {props.isPast && props.hasCanonicalVideo
                  ? 'Watch on Tradewing'
                  : 'Join on Tradewing'}
              </Button>
            </NextLink>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
